import React, { useEffect, useMemo } from "react";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function daysInMonth(year, month) {
  if (!month) return 31;
  if (!year) {
    const m = Number(month);
    if (m === 2) return 29;
    if ([4, 6, 9, 11].includes(m)) return 30;
    return 31;
  }
  return new Date(Number(year), Number(month), 0).getDate();
}

function parseDob(value) {
  const m = (value || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return { day: "", month: "", year: "" };
  return {
    day: m[1].padStart(2, "0"),
    month: m[2].padStart(2, "0"),
    year: m[3],
  };
}

function composeDob(day, month, year) {
  if (!day || !month || !year || year.length !== 4) return "";
  return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
}

/**
 * Dropdown day / month / year picker — easier for birth dates than calendar month-hopping.
 * `value` / `onChange` use DD/MM/YYYY (backend format).
 */
const DateOfBirthPicker = ({ value, onChange, "data-testid": testId = "dob-picker" }) => {
  const parsed = useMemo(() => parseDob(value), [value]);
  const [day, setDay] = React.useState(parsed.day);
  const [month, setMonth] = React.useState(parsed.month);
  const [year, setYear] = React.useState(parsed.year);

  useEffect(() => {
    setDay(parsed.day);
    setMonth(parsed.month);
    setYear(parsed.year);
  }, [parsed.day, parsed.month, parsed.year]);

  const years = useMemo(() => {
    const max = new Date().getFullYear() - 5;
    const min = 1925;
    const list = [];
    for (let y = max; y >= min; y -= 1) list.push(String(y));
    return list;
  }, []);

  const maxDay = daysInMonth(year, month);
  const dayOptions = useMemo(() => {
    return Array.from({ length: maxDay }, (_, i) => String(i + 1).padStart(2, "0"));
  }, [maxDay]);

  const emit = (d, m, y) => {
    onChange?.(composeDob(d, m, y));
  };

  const handleMonth = (m) => {
    setMonth(m);
    let d = day;
    if (d && Number(d) > daysInMonth(year, m)) {
      d = String(daysInMonth(year, m)).padStart(2, "0");
      setDay(d);
    }
    emit(d, m, year);
  };

  const handleYear = (y) => {
    setYear(y);
    let d = day;
    if (d && month && Number(d) > daysInMonth(y, month)) {
      d = String(daysInMonth(y, month)).padStart(2, "0");
      setDay(d);
    }
    emit(d, month, y);
  };

  const selectClass =
    "w-full bg-spike-elevated border border-white/10 px-3 py-3 text-spike-text focus:border-spike-blue focus:outline-none font-exo text-sm appearance-none cursor-pointer";

  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-[0.55rem] font-orbitron uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">
            Month
          </label>
          <select
            data-testid={`${testId}-month`}
            value={month}
            onChange={(e) => handleMonth(e.target.value)}
            className={selectClass}
          >
            <option value="">Month</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[0.55rem] font-orbitron uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">
            Day
          </label>
          <select
            data-testid={`${testId}-day`}
            value={day}
            onChange={(e) => {
              setDay(e.target.value);
              emit(e.target.value, month, year);
            }}
            className={selectClass}
            disabled={!month}
          >
            <option value="">Day</option>
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                {Number(d)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[0.55rem] font-orbitron uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">
            Year
          </label>
          <select
            data-testid={`${testId}-year`}
            value={year}
            onChange={(e) => handleYear(e.target.value)}
            className={selectClass}
          >
            <option value="">Year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
      {value ? (
        <p className="mt-2 text-[0.65rem] font-exo text-spike-text-muted" data-testid={`${testId}-preview`}>
          Selected: {value}
        </p>
      ) : (
        <p className="mt-2 text-[0.65rem] font-exo text-spike-dim italic">
          Pick month, day, and year — no calendar scrolling needed.
        </p>
      )}
    </div>
  );
};

export default DateOfBirthPicker;
